/**
 * Carregamento do manifesto e revalidação.
 *
 * Só baixa quando a camada é ligada. Depois, revalida:
 * - a cada ciclo da atualização automática (gancho do loop);
 * - quando a janela do loop anda (chegou imagem nova do satélite) — é nessa
 *   hora que o quadro novo precisa da varredura nova. Os ganchos da
 *   atualização automática não rodam quando a janela muda, então este caso
 *   é tratado aqui.
 */
import { subscribeSelector } from '../lib/store';
import { useTimeStore } from '../state/time';
import { useNow } from '../ui/hooks';
import { carregarManifesto, type Manifesto } from './api';
import { radarCache } from './cache';
import { frescorDe } from './frescor';
import { useRadarStore } from './store';

/** Revalidações a menos disto do último pedido são ignoradas. */
const INTERVALO_MIN_MS = 55_000;

let emVoo: AbortController | null = null;
let jaCarregou = false;
let ultimoPedido = 0;

function mesmoManifesto(a: Manifesto | null, b: Manifesto): boolean {
  if (!a || a.gerado === null || a.gerado !== b.gerado || a.radares.length !== b.radares.length) return false;
  return a.radares.every((r, i) => {
    const s = b.radares[i];
    return r.id === s.id && r.extent.join() === s.extent.join() && r.produtos.dbz.t.length === s.produtos.dbz.t.length &&
      r.produtos.rate.t.length === s.produtos.rate.t.length;
  });
}

export async function carregarRadar(force = false): Promise<void> {
  const { situacao } = useRadarStore.getState();
  if (!force && (situacao.estado === 'loading' || jaCarregou)) return;
  emVoo?.abort();
  const ctrl = new AbortController();
  emVoo = ctrl;
  ultimoPedido = Date.now();
  // A revalidação é silenciosa: "Baixando…" piscando a cada ciclo é ruído.
  if (!jaCarregou) useRadarStore.getState().setSituacao({ estado: 'loading', erro: null });
  try {
    const m = await carregarManifesto(ctrl.signal);
    if (ctrl.signal.aborted) return;
    jaCarregou = true;
    // Publicação igual à que está na tela: não trocar poupa recalcular o plano.
    if (!mesmoManifesto(useRadarStore.getState().manifesto, m)) useRadarStore.getState().setManifesto(m);
    useRadarStore.getState().setSituacao({ estado: 'ready', erro: null });
  } catch (e) {
    if (ctrl.signal.aborted) return;
    // Revalidação que falhou com dado na tela: mantém o dado e mostra o erro.
    useRadarStore.getState().setSituacao({ estado: 'error', erro: e instanceof Error ? e.message : String(e) });
  } finally {
    if (emVoo === ctrl) emVoo = null;
  }
}

/**
 * Revalidação do ciclo automático — só com a camada em uso. Também
 * recoloca na fila as varreduras que falharam (ex.: a conexão caiu por um
 * minuto) e tenta de novo o manifesto que nunca chegou.
 */
export function refreshRadar(): void {
  if (!useRadarStore.getState().visivel) return;
  radarCache.retryFailed();
  const nuncaCarregou = !jaCarregou && useRadarStore.getState().situacao.estado === 'error';
  if (!jaCarregou && !nuncaCarregou) return;
  if (Date.now() - ultimoPedido < INTERVALO_MIN_MS) return;
  void carregarRadar(true);
}

export function iniciarSyncRadar(): () => void {
  if (useRadarStore.getState().visivel) void carregarRadar();
  const aoVoltarConexao = () => {
    ultimoPedido = 0;
    refreshRadar();
  };
  if (typeof window !== 'undefined') window.addEventListener('online', aoVoltarConexao);
  const unsubs = [
    useRadarStore.subscribe((a, b) => {
      if (!a.visivel || b.visivel) return;
      // Religar depois de um tempo: o manifesto guardado pode ser de horas atrás.
      if (!jaCarregou) void carregarRadar();
      else if (Date.now() - ultimoPedido >= INTERVALO_MIN_MS) void carregarRadar(true);
    }),
    subscribeSelector(useTimeStore, (s) => s.range?.end ?? null, refreshRadar),
  ];
  return () => {
    unsubs.forEach((u) => u());
    if (typeof window !== 'undefined') window.removeEventListener('online', aoVoltarConexao);
  };
}

/** Ponto amarelo no trilho: camada ligada e dado com problema. */
export function useRadarDesatualizado(): boolean {
  const visivel = useRadarStore((s) => s.visivel);
  const erro = useRadarStore((s) => s.situacao.estado === 'error' && !s.manifesto);
  const gerado = useRadarStore((s) => s.manifesto?.gerado ?? null);
  // Relógio próprio: com o espelho parado o manifesto não muda, e sem isto o
  // ponto amarelo nunca apareceria com a página aberta.
  const agora = useNow(60_000);
  if (!visivel) return false;
  const f = frescorDe(gerado, agora);
  return erro || f === 'atrasado' || f === 'velho';
}

/** Só para os testes. */
export function resetRadarSync(): void {
  emVoo?.abort();
  emVoo = null;
  jaCarregou = false;
  ultimoPedido = 0;
}
